import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Cloud, Download, Loader2, FileText, Image as ImageIcon, Film,
  File as FileIcon, Folder, Upload, Eye, X, CheckCircle2, List, LayoutGrid
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const formatBytes = (b) => {
  if (!b) return '0 B';
  if (b > 1024 * 1024 * 1024) return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB';
  if (b > 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
  if (b > 1024) return (b / 1024).toFixed(0) + ' KB';
  return b + ' B';
};
const formatEta = (sec) => {
  if (sec === null || sec === undefined) return '';
  if (sec < 60) return `~${sec}sn`;
  const m = Math.floor(sec / 60);
  return `~${m}dk ${sec % 60}sn`;
};
const kindIcon = (k) => {
  if (k === 'image') return ImageIcon;
  if (k === 'video') return Film;
  if (k === 'pdf') return FileText;
  return FileIcon;
};

export default function PublicCloudSharePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);
  const downloadXhrRef = useRef(null);
  const uploadXhrsRef = useRef({});

  // View mode & sort (persisted)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('cloud-pub-view-mode') || 'details');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('cloud-pub-sort') || 'name-asc');
  useEffect(() => { localStorage.setItem('cloud-pub-view-mode', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('cloud-pub-sort', sortBy); }, [sortBy]);

  const sortedFiles = useMemo(() => {
    const arr = [...(info?.files || [])];
    arr.sort((x, y) => {
      if (sortBy === 'name-asc') return (x.name || '').localeCompare(y.name || '', 'tr');
      if (sortBy === 'name-desc') return (y.name || '').localeCompare(x.name || '', 'tr');
      if (sortBy === 'date-asc') return (x.created_at || '').localeCompare(y.created_at || '');
      return (y.created_at || '').localeCompare(x.created_at || '');
    });
    return arr;
  }, [info, sortBy]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const loadInfo = async () => {
    try {
      const r = await fetch(`${API}/cloud/share/${token}/info`);
      if (!r.ok) {
        if (r.status === 404) throw new Error('Bu link geçersiz veya kaldırılmış');
        throw new Error('Yüklenemedi');
      }
      setInfo(await r.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadInfo(); }, [token]);

  const startDownload = (downloadAll = false, fileObj = null) => {
    const url = downloadAll || !fileObj
      ? `${API}/cloud/share/${token}/download`
      : `${API}/cloud/share/${token}/file/${fileObj.id}`;
    const isZip = (downloadAll || !fileObj) && info?.type === 'folder';

    // Doğrudan tarayıcının indirme yöneticisine devret — bellekte tutmadan diske akar.
    // Klasör ZIP'i için sunucu önce sıkıştırır, tarayıcı "Bağlanıyor..." gösterir; sonra indirme akar.
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 1000);

    if (isZip) {
      setProgress({ status: 'preparing', pct: 0, loaded: 0, total: 0, speed: 0, eta: null, name: info?.name || '' });
      toast.success('Sıkıştırma başlatıldı — hazır olunca tarayıcı indirme bölümünde görünecek');
      // 12 saniye sonra göstergeyi kaldır (sıkıştırma genelde bu sürede başlar)
      setTimeout(() => setProgress(null), 12000);
    } else {
      toast.success('İndirme başlatıldı — tarayıcının indirme bölümünden takip edebilirsiniz');
    }
  };

  const cancelDownload = () => {
    // Browser native indirme yöneticisi kullanılıyor; iptal sadece sıkıştırma göstergesini kapatır.
    setProgress(null);
  };

  const handlePublicFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of list) {
      try {
        const params = new URLSearchParams({ name: f.name, size: String(f.size) });
        const r = await fetch(`${API}/cloud/share/${token}/exists?${params}`);
        if (r.ok) {
          const data = await r.json();
          if (data.exists) {
            const msg = data.same_size
              ? `"${f.name}" adında VE aynı boyutta (${formatBytes(f.size)}) bir dosya zaten var.\n\nYine de yüklensin mi?`
              : `"${f.name}" adında bir dosya zaten var.\n\nYine de yüklensin mi?`;
            if (!window.confirm(msg)) continue;
          }
        }
      } catch { /* sessizce geç */ }
      uploadFile(f);
    }
  };

  const uploadFile = (file) => {
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const fd = new FormData();
    fd.append('file', file);

    const xhr = new XMLHttpRequest();
    uploadXhrsRef.current[uid] = xhr;
    xhr.open('POST', `${API}/cloud/share/${token}/upload`, true);
    const startedAt = Date.now();

    setUploads(prev => [...prev, { id: uid, name: file.name, pct: 0, loaded: 0, total: file.size, speed: 0, eta: null, status: 'uploading' }]);

    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable) return;
      const pct = Math.round((ev.loaded / ev.total) * 100);
      const elapsed = (Date.now() - startedAt) / 1000;
      let speed = 0, eta = null;
      if (elapsed > 0.3) {
        speed = ev.loaded / elapsed;
        if (speed > 0) eta = Math.max(0, Math.round((ev.total - ev.loaded) / speed));
      }
      setUploads(prev => prev.map(u => u.id === uid ? { ...u, pct, loaded: ev.loaded, total: ev.total, speed, eta } : u));
    };
    xhr.onload = () => {
      delete uploadXhrsRef.current[uid];
      if (xhr.status >= 200 && xhr.status < 300) {
        setUploads(prev => prev.map(u => u.id === uid ? { ...u, status: 'done', pct: 100 } : u));
        loadInfo();
      } else {
        toast.error(`${file.name} yüklenemedi (HTTP ${xhr.status})`);
        setUploads(prev => prev.map(u => u.id === uid ? { ...u, status: 'error' } : u));
      }
      setTimeout(() => setUploads(prev => prev.filter(u => u.id !== uid)), 2500);
    };
    xhr.onerror = () => {
      delete uploadXhrsRef.current[uid];
      toast.error(`${file.name} yüklenemedi`);
      setUploads(prev => prev.filter(u => u.id !== uid));
    };
    xhr.onabort = () => {
      delete uploadXhrsRef.current[uid];
      setUploads(prev => prev.filter(u => u.id !== uid));
    };
    xhr.send(fd);
  };

  const cancelUpload = (uid) => {
    const xhr = uploadXhrsRef.current[uid];
    if (xhr) { xhr.abort(); toast.info('Yükleme iptal edildi'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-blue-50">
      <Loader2 className="h-10 w-10 animate-spin text-sky-500" />
    </div>
  );

  if (error || !info) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-50 p-4">
      <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
        <Cloud className="h-16 w-16 text-red-300 mx-auto mb-3" />
        <h1 className="text-xl font-bold mb-2">Link bulunamadı</h1>
        <p className="text-slate-500 text-sm">{error}</p>
      </CardContent></Card>
    </div>
  );

  const isFolder = info.type === 'folder';
  const isFile = info.type === 'file';
  const isImagePreview = isFile && info.kind === 'image';
  const isVideoPreview = isFile && info.kind === 'video';
  const Ic = isFolder ? Folder : kindIcon(info.kind);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-100 p-3 sm:p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        <Card className="shadow-2xl overflow-hidden" data-testid="public-share-card">
          <div className="bg-gradient-to-r from-sky-500 to-blue-600 text-white p-4 flex items-center gap-3">
            <Cloud className="h-8 w-8" />
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold truncate">Bulut Paylaşımı</h1>
              <p className="text-xs opacity-90">AkıllıÇözüm Bulut üzerinden paylaşıldı</p>
            </div>
            {info.allow_upload && (
              <span className="bg-white/20 backdrop-blur px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1">
                <Upload className="h-3 w-3" />Yükleme Açık
              </span>
            )}
          </div>
          <CardContent className="p-4 sm:p-6 space-y-4">
            {/* Tek dosya paylaşımı */}
            {isFile && (
              <>
                <div className="bg-slate-50 rounded-lg overflow-hidden flex items-center justify-center min-h-[200px]">
                  {isImagePreview && <img src={`${API}/cloud/share/${token}/preview`} alt={info.name} className="max-w-full max-h-[400px]" />}
                  {isVideoPreview && <video src={`${API}/cloud/share/${token}/preview`} controls className="max-w-full max-h-[400px]" />}
                  {!isImagePreview && !isVideoPreview && (
                    <div className="p-8 text-center">
                      <Ic className="h-24 w-24 text-sky-400 mx-auto mb-2" />
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-bold break-all">{info.name}</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {formatBytes(info.size)}
                    {info.download_count > 0 && ` · ${info.download_count} kere indirildi`}
                  </p>
                </div>
              </>
            )}

            {/* Klasör paylaşımı: meta + dosya grid */}
            {isFolder && (
              <>
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                  <Folder className="h-12 w-12 text-sky-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h2 className="text-lg font-bold break-all">{info.name}</h2>
                    <p className="text-xs text-slate-500">
                      {info.file_count} dosya · {formatBytes(info.size)}
                      {info.download_count > 0 && ` · ${info.download_count} indirildi`}
                      {info.upload_count > 0 && ` · ${info.upload_count} yükleme`}
                    </p>
                  </div>
                  {info.file_count > 0 && (
                    progress ? (
                      <div className="flex-shrink-0 min-w-[260px] max-w-[360px] bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 space-y-1"
                        data-testid="public-download-progress">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-bold text-emerald-700 flex items-center gap-1 truncate">
                            <Download className="h-3.5 w-3.5 animate-bounce flex-shrink-0" />
                            {progress.status === 'preparing' ? 'Sıkıştırılıyor...'
                              : progress.status === 'connecting' ? 'Bağlanıyor...'
                              : progress.status === 'done' ? 'Tamamlandı'
                              : `İndiriliyor %${progress.pct}`}
                          </span>
                          {progress.status !== 'done' && (
                            <button onClick={cancelDownload}
                              className="p-0.5 hover:bg-red-100 rounded text-red-500 flex-shrink-0"
                              title="İptal" data-testid="cancel-download-btn">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all ${
                            (progress.status === 'preparing' || progress.status === 'connecting') ? 'animate-pulse w-1/3' : ''
                          }`}
                            style={(progress.status === 'preparing' || progress.status === 'connecting') ? {} : { width: `${progress.pct}%` }} />
                        </div>
                        {progress.status === 'downloading' && (
                          <div className="text-[10px] text-slate-600 truncate">
                            {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
                            {progress.speed > 0 && ` · ${formatBytes(progress.speed)}/sn`}
                            {progress.eta !== null && ` · ${formatEta(progress.eta)}`}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Button onClick={() => startDownload(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="public-download-all-btn">
                        <Download className="h-4 w-4 mr-1" />Hepsini İndir (ZIP)
                      </Button>
                    )
                  )}
                </div>

                {/* Upload section */}
                {info.allow_upload && (
                  <Card className="border-2 border-dashed border-emerald-300 bg-emerald-50/40">
                    <CardContent className="p-4 text-center space-y-2">
                      <Upload className="h-10 w-10 text-emerald-600 mx-auto" />
                      <div>
                        <p className="font-bold text-emerald-700">Dosya Yükleyebilirsiniz</p>
                        <p className="text-xs text-emerald-600 mt-0.5">
                          Yüklediğiniz dosyalar bu klasörde görünür ve paylaşımı yapan kişi tarafından indirilebilir.
                        </p>
                      </div>
                      <input type="file" multiple ref={fileInputRef} onChange={handlePublicFiles} className="hidden" data-testid="public-upload-input" />
                      <Button onClick={() => fileInputRef.current?.click()}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="public-upload-btn">
                        <Upload className="h-4 w-4 mr-2" />Dosya Seç ve Yükle
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Upload progress */}
                {uploads.length > 0 && (
                  <Card className="bg-emerald-50/40 border-emerald-200">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Yükleniyor ({uploads.length})
                      </p>
                      {uploads.map(u => (
                        <div key={u.id} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="truncate flex-1 flex items-center gap-1">
                              {u.status === 'done' && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                              {u.name}
                            </span>
                            <span className="ml-2 text-slate-600 whitespace-nowrap">
                              {u.pct}% · {formatBytes(u.loaded)} / {formatBytes(u.total)}
                              {u.speed > 0 && ` · ${formatBytes(u.speed)}/sn`}
                              {u.eta !== null && ` · ${formatEta(u.eta)}`}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all"
                              style={{ width: `${u.pct}%` }} />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Files grid */}
                {(info.files || []).length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <FileIcon className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    Bu klasörde henüz dosya yok
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2 bg-white border rounded-lg px-3 py-2">
                      <div className="text-xs text-slate-500"><b>{sortedFiles.length}</b> dosya</div>
                      <div className="flex items-center gap-2">
                        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                          className="text-xs border rounded px-2 py-1 bg-white cursor-pointer hover:border-sky-400 focus:outline-none focus:border-sky-500"
                          data-testid="pub-sort-select">
                          <option value="date-desc">Tarih (Yeni → Eski)</option>
                          <option value="date-asc">Tarih (Eski → Yeni)</option>
                          <option value="name-asc">İsim (A → Z)</option>
                          <option value="name-desc">İsim (Z → A)</option>
                        </select>
                        <div className="flex border rounded overflow-hidden">
                          <button onClick={() => setViewMode('details')} title="Detay (hızlı)"
                            className={`p-1.5 ${viewMode === 'details' ? 'bg-sky-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                            data-testid="pub-view-details">
                            <List className="h-4 w-4" />
                          </button>
                          <button onClick={() => setViewMode('thumbs')} title="Küçük resim"
                            className={`p-1.5 ${viewMode === 'thumbs' ? 'bg-sky-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                            data-testid="pub-view-thumbs">
                            <LayoutGrid className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {viewMode === 'details' ? (
                      <Card>
                        <div className="divide-y">
                          {sortedFiles.map(f => {
                            const FIc = kindIcon(f.kind);
                            return (
                              <div key={f.id} className="flex items-center gap-3 px-3 py-2 hover:bg-emerald-50 transition group"
                                data-testid={`pub-file-${f.id}`}>
                                <FIc className={`h-6 w-6 flex-shrink-0 cursor-pointer ${
                                  f.kind === 'image' ? 'text-pink-500'
                                  : f.kind === 'video' ? 'text-purple-500'
                                  : f.kind === 'pdf' ? 'text-red-500'
                                  : 'text-slate-400'
                                }`} onClick={() => setPreview(f)} />
                                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setPreview(f)}>
                                  <p className="font-medium truncate text-sm">{f.name}</p>
                                  <p className="text-[11px] text-slate-500">
                                    {formatBytes(f.size)}{f.created_at && ` · ${formatDate(f.created_at)}`}
                                  </p>
                                </div>
                                <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition">
                                  <button onClick={() => setPreview(f)} title="Önizle" className="p-1.5 hover:bg-slate-100 rounded">
                                    <Eye className="h-3.5 w-3.5 text-slate-500" />
                                  </button>
                                  <button onClick={() => startDownload(false, f)} title="İndir"
                                    className="p-1.5 hover:bg-emerald-100 rounded" data-testid={`pub-download-${f.id}`}>
                                    <Download className="h-3.5 w-3.5 text-emerald-600" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {sortedFiles.map(f => {
                          const FIc = kindIcon(f.kind);
                          return (
                            <Card key={f.id} className="overflow-hidden hover:shadow-md transition group" data-testid={`pub-file-${f.id}`}>
                              <div className="aspect-square bg-slate-100 relative flex items-center justify-center cursor-pointer"
                                onClick={() => setPreview(f)}>
                                {f.kind === 'image' ? (
                                  <img src={`${API}/cloud/share/${token}/file/${f.id}`}
                                    alt={f.name}
                                    loading="lazy"
                                    decoding="async"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                    className="w-full h-full object-cover" />
                                ) : (
                                  <FIc className="h-16 w-16 text-slate-400" />
                                )}
                                {f.kind === 'video' && (
                                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                    <Film className="h-12 w-12 text-white" />
                                  </div>
                                )}
                              </div>
                              <CardContent className="p-2">
                                <p className="text-xs font-medium truncate">{f.name}</p>
                                <p className="text-[10px] text-slate-500">{formatBytes(f.size)}</p>
                                <div className="flex items-center gap-1 mt-1.5">
                                  <button onClick={() => setPreview(f)} title="Önizle"
                                    className="flex-1 p-1.5 hover:bg-slate-100 rounded flex items-center justify-center">
                                    <Eye className="h-3.5 w-3.5 text-slate-500" />
                                  </button>
                                  <button onClick={() => startDownload(false, f)} title="İndir"
                                    className="flex-1 p-1.5 hover:bg-emerald-100 rounded flex items-center justify-center"
                                    data-testid={`pub-download-${f.id}`}>
                                    <Download className="h-3.5 w-3.5 text-emerald-600" />
                                  </button>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Download progress (sadece tek dosya modu için altta görünür; klasör modunda buton hizasında inline gösteriliyor) */}
            {progress && !isFolder && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1" data-testid="public-download-progress-file">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-emerald-700 flex items-center gap-1">
                    <Download className="h-3.5 w-3.5 animate-bounce" />
                    İndiriliyor... {progress.pct}%
                    {progress.name && <span className="font-normal text-xs text-slate-600">· {progress.name}</span>}
                  </span>
                  <span className="text-xs text-slate-600 whitespace-nowrap">
                    {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
                    {progress.speed > 0 && ` · ${formatBytes(progress.speed)}/sn`}
                    {progress.eta !== null && ` · ${formatEta(progress.eta)}`}
                  </span>
                </div>
                <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all relative overflow-hidden"
                    style={{ width: `${progress.pct}%` }}>
                    <div className="absolute inset-0 bg-white/20 animate-pulse" />
                  </div>
                </div>
              </div>
            )}

            {/* Tek dosya için download butonu */}
            {isFile && !progress && (
              <Button onClick={() => startDownload(true)}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white py-6 text-base font-bold"
                data-testid="public-download-btn">
                <Download className="h-5 w-5 mr-2" />İndir ({formatBytes(info.size)})
              </Button>
            )}

            <p className="text-[10px] text-center text-slate-400">
              Bu link sahibi, paylaşımı herhangi bir zaman kaldırabilir.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 truncate">
              {preview && (() => {
                const FIc = kindIcon(preview.kind);
                return <FIc className="h-5 w-5 flex-shrink-0" />;
              })()}
              <span className="truncate">{preview?.name}</span>
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-2">
              <div className="bg-slate-50 rounded-lg p-2 flex items-center justify-center min-h-[200px]">
                {preview.kind === 'image' && (
                  <img src={`${API}/cloud/share/${token}/file/${preview.id}`} alt={preview.name} className="max-w-full max-h-[60vh] rounded" />
                )}
                {preview.kind === 'video' && (
                  <video src={`${API}/cloud/share/${token}/file/${preview.id}`} controls className="max-w-full max-h-[60vh] rounded" />
                )}
                {preview.kind === 'audio' && (
                  <audio src={`${API}/cloud/share/${token}/file/${preview.id}`} controls />
                )}
                {(preview.kind === 'pdf' || preview.kind === 'file') && (
                  <div className="text-center p-8">
                    <FileText className="h-16 w-16 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">{preview.name}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => startDownload(false, preview)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="h-4 w-4 mr-2" />İndir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
